import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaStar, FaPhone, FaUser, FaTools, FaTimes } from "react-icons/fa"; 

const ServicemenReviews = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { servicemanId } = location.state || {};
  const [serviceman, setServiceman] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [servicemanError, setServicemanError] = useState(null);
  const [reviewsError, setReviewsError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPhoto, setSelectedPhoto] = useState(null); 
  const reviewsPerPage = 5;
  const DEFAULT_IMAGE = "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png";

  useEffect(() => {
    if (!servicemanId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const fetchServiceman = fetch(`https://us-central1-workexpress-1ffff.cloudfunctions.net/app/servicemen/${servicemanId}`)
      .then((response) => {
        if (!response.ok) {
          return response.text().then((text) => {
            throw new Error(`Failed to fetch serviceman details: ${response.status} - ${text || response.statusText}`);
          });
        }
        return response.json();
      })
      .then((data) => setServiceman(data))
      .catch((error) => {
        console.error("Error fetching serviceman:", error);
        setServicemanError(error.message);
      });

    const fetchReviews = fetch(`https://us-central1-workexpress-1ffff.cloudfunctions.net/app/servicemen/${servicemanId}/reviews`)
      .then((response) => {
        if (response.status === 404) {
          return [];
        }
        if (!response.ok) {
          return response.text().then((text) => {
            throw new Error(`Failed to fetch reviews: ${response.status} - ${text || response.statusText}`);
          });
        }
        return response.json();
      })
      .then((data) => {
        const reviewsData = Array.isArray(data) ? data : [];
        setReviews(reviewsData);

        const uniqueUids = [...new Set(reviewsData.map((review) => review.uid))];
        const userPromises = uniqueUids.map((uid) =>
          fetch(`https://us-central1-workexpress-1ffff.cloudfunctions.net/app/users/${uid}`)
            .then((response) => {
              if (!response.ok) {
                return response.text().then((text) => {
                  throw new Error(`Failed to fetch user ${uid}: ${response.status} - ${text || response.statusText}`);
                });
              }
              return response.json();
            })
            .then((userData) => ({
              uid,
              name: userData.name || "Unknown User",
              image: userData.image ? `data:image/jpeg;base64,${userData.image}` : DEFAULT_IMAGE,
            }))
            .catch((error) => {
              console.error(`Error fetching user ${uid}:`, error);
              return { uid, name: "Unknown User", image: DEFAULT_IMAGE };
            })
        );

        return Promise.all(userPromises);
      })
      .then((userResults) => {
        const usersMap = userResults.reduce((acc, { uid, name, image }) => {
          acc[uid] = { name, image };
          return acc;
        }, {});
        setUsers(usersMap);
      })
      .catch((error) => {
        console.error("Error fetching reviews or users:", error);
        setReviewsError(error.message);
      });

    Promise.all([fetchServiceman, fetchReviews])
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  }, [servicemanId]);

  const indexOfLastReview = currentPage * reviewsPerPage;
  const indexOfFirstReview = indexOfLastReview - reviewsPerPage;
  const currentReviews = reviews.slice(indexOfFirstReview, indexOfLastReview);
  const totalPages = Math.ceil(reviews.length / reviewsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePhotoClick = (photo) => {
    setSelectedPhoto(photo);
  };

  const handleCloseModal = () => {
    setSelectedPhoto(null);
  };

  if (!servicemanId) {
    return (
      <div className="error-container">
        <p>No serviceman ID provided</p>
        <button onClick={() => navigate("/servicemen")}>Back to Servicemen</button>
        <style>{`
          .error-container {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
            color: #dc2626;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          }
          button {
            margin-top: 20px;
            padding: 10px 20px;
            background: #3b82f6;
            color: #fff;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            transition: background 0.3s ease;
            font-size: 16px;
          }
          button:hover {
            background: #2563eb;
          }
        `}</style>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading-container">
        <span className="loader"></span>
        <style>{`
          .loading-container {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            background: #f9fafb;
            color: #4a5568;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          }
          .loader {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            display: inline-block;
            position: relative;
            border: 2px solid #FF3D00;
            box-sizing: border-box;
            animation: rotation 1s linear infinite;
          }
          .loader::after {
            content: '';  
            box-sizing: border-box;
            position: absolute;
            left: 4px;
            top: 4px;
            border: 2px solid #000;
            width: 12px;
            height: 12px;
            border-radius: 50%;
          }
          [data-theme="dark"] .loader::after {
            border: 2px solid #FFF;
          }
          @keyframes rotation {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </div>
    );
  }

  if (servicemanError && reviewsError) {
    return (
      <div className="error-container">
        <p>{servicemanError}</p>
        <p>{reviewsError}</p>
        <button onClick={() => navigate("/servicemen")}>Back to Servicemen</button>
        <style>{`
          .error-container {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
            color: #dc2626;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          }
          button {
            margin-top: 20px;
            padding: 10px 20px;
            background: #3b82f6;
            color: #fff;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            transition: background 0.3s ease;
            font-size: 16px;
          }
          button:hover {
            background: #2563eb;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="reviews-panel">
      <style>{`
        .reviews-panel {
          padding: 30px;
          max-width: 900px;
          margin: 30px auto;
          background: #ffffff;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          background: linear-gradient(135deg, #ffffff 0%, #f9fafb 100%);
        }

        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-bottom: 20px;
          border-bottom: 2px solid #e5e7eb;
          margin-bottom: 25px;
        }

        h2 {
          color: #1f2937;
          font-size: 28px;
          font-weight: 700;
          margin: 0;
          letter-spacing: -0.5px;
        }

        .back-btn {
          padding: 10px 20px;
          background: #3b82f6;
          color: #fff;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 16px;
          font-weight: 500;
          transition: background 0.3s ease, transform 0.2s ease;
        }

        .back-btn:hover {
          background: #2563eb;
          transform: translateY(-2px);
        }

        .serviceman-info {
          margin-bottom: 30px;
          padding: 20px;
          background: #fff;
          border-radius: 10px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
          border: 1px solid #e5e7eb;
          transition: box-shadow 0.3s ease;
        }

        .serviceman-info:hover {
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.07);
        }

        .info-item {
          display: flex;
          align-items: center;
          margin: 10px 0;
          color: #374151;
          font-size: 16px;
        }

        .info-icon {
          margin-right: 10px;
          color: #6b7280;
        }

        .info-label {
          font-weight: 600;
          color: #4a5568;
          margin-right: 8px;
        }

        .rating {
          color: #f59e0b;
          font-weight: 600;
          display: flex;
          align-items: center;
        }

        .rating .star {
          margin-left: 5px;
        }

        .reviews-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .review-item {
          padding: 20px;
          border-bottom: 1px solid #e5e7eb;
          background: #fff;
          border-radius: 10px;
          margin-bottom: 15px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
          transition: transform 0.2s ease, box-shadow 0.3s ease;
        }

        .review-item:hover {
          transform: translateY(-3px);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.07);
        }

        .review-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }

        .reviewer-info {
          display: flex;
          align-items: center;
        }

        .reviewer-image {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          object-fit: cover;
          margin-right: 10px;
          border: 1px solid #e5e7eb;
        }

        .reviewer-name {
          color: #1f2937;
          font-weight: 600;
          font-size: 16px;
          margin-right: 10px;
        }

        .date {
          color: #6b7280;
          font-size: 14px;
          font-style: italic;
        }

        .review-text {
          color: #374151;
          font-size: 16px;
          line-height: 1.5;
          margin: 10px 0;
        }

        .photos-container {
          display: flex;
          flex-wrap: wrap;
          gap: 15px;
          margin-top: 15px;
        }

        .review-photo {
          width: 120px;
          height: 120px;
          object-fit: cover;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          transition: transform 0.2s ease;
          cursor: pointer;
        }

        .review-photo:hover {
          transform: scale(1.05);
        }

        .no-reviews {
          text-align: center;
          color: #6b7280;
          padding: 30px;
          font-size: 18px;
          font-style: italic;
        }

        .error-message {
          color: #dc2626;
          font-size: 16px;
          margin: 15px 0;
          padding: 15px;
          background: #fee2e2;
          border-radius: 8px;
          text-align: center;
        }

        .pagination {
          margin-top: 30px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          color: #4a5568;
          font-size: 16px;
        }

        .pagination-buttons {
          display: flex;
          gap: 10px;
        }

        .page-btn {
          padding: 8px 16px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: #fff;
          cursor: pointer;
          color: #374151;
          font-size: 14px;
          transition: background 0.3s ease, color 0.3s ease;
        }

        .page-btn:hover:not(.active) {
          background: #e5e7eb;
        }

        .page-btn.active {
          background: #3b82f6;
          color: #fff;
          border-color: #3b82f6;
        }

        .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .photo-modal {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .modal-content {
          position: relative;
          max-width: 80%;
          max-height: 80%;
          border-radius: 10px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
        }

        .modal-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .close-btn {
          position: absolute;
          top: 15px;
          right: 15px;
          background: #fff;
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          justify-content: center;
          align-items: center;
          cursor: pointer;
          font-size: 20px;
          color: #1f2937;
          transition: background 0.3s ease, transform 0.2s ease;
        }

        .close-btn:hover {
          background: #e5e7eb;
          transform: scale(1.1);
        }

        @media (max-width: 768px) {
          .reviews-panel {
            padding: 20px;
            margin: 20px 10px;
          }

          h2 {
            font-size: 24px;
          }

          .serviceman-info {
            padding: 15px;
          }

          .info-item {
            font-size: 14px;
            flex-direction: column;
            align-items: flex-start;
          }

          .review-item {
            padding: 15px;
          }

          .review-photo {
            width: 90px;
            height: 90px;
          }

          .reviewer-image {
            width: 30px;
            height: 30px;
          }

          .pagination {
            flex-direction: column;
            gap: 15px;
          }

          .modal-content {
            max-width: 90%;
            max-height: 90%;
          }
        }
      `}</style>

      <div className="header">
        <h2>Reviews for Serviceman</h2>
        <button className="back-btn" onClick={() => navigate("/servicemen")}>
          Back
        </button>
      </div>

      {servicemanError && <p className="error-message">{servicemanError}</p>}

      {serviceman && (
        <div className="serviceman-info">
          <div className="info-item">
            <FaUser className="info-icon" />
            <span className="info-label">Name:</span> {serviceman.name || "N/A"}
          </div>
          <div className="info-item">
            <FaPhone className="info-icon" />
            <span className="info-label">Phone:</span> {serviceman.phone || "N/A"}
          </div>
          <div className="info-item">
            <FaTools className="info-icon" />
            <span className="info-label">Profession:</span> {serviceman.profession || "N/A"}
          </div>
          <div className="info-item">
            <span className="info-label">Average Rating:</span>
            <span className="rating">
              {serviceman.avgRating ? `${serviceman.avgRating}/5` : "N/A"}
              {serviceman.avgRating && <FaStar className="star" />}
            </span>
          </div>
        </div>
      )}

      {reviewsError && <p className="error-message">{reviewsError}</p>}

      <ul className="reviews-list">
        {reviews.length > 0 ? (
          currentReviews.map((review) => {
            const createdAt = review.createdAt?._seconds
              ? new Date(review.createdAt._seconds * 1000).toLocaleString()
              : "N/A";
            const reviewer = users[review.uid] || { name: "Unknown User", image: DEFAULT_IMAGE };

            return (
              <li key={review.id} className="review-item">
                <div className="review-header">
                  <div className="reviewer-info">
                    <img
                      src={reviewer.image}
                      alt={`${reviewer.name}'s profile`}
                      className="reviewer-image"
                      onError={(e) => {
                        e.target.src = DEFAULT_IMAGE;
                      }}
                    />
                    <div>
                      <span className="reviewer-name">{reviewer.name}</span>
                      <span className="rating">
                        {review.rating}/5 <FaStar className="star" />
                      </span>
                    </div>
                  </div>
                  <span className="date">{createdAt}</span>
                </div>
                <p className="review-text">{review.review || "No review text provided"}</p>
                {review.photos?.length > 0 && (
                  <div className="photos-container">
                    {review.photos.map((photo, index) => (
                      <img
                        key={index}
                        src={photo}
                        alt={`Review photo ${index + 1}`}
                        className="review-photo"
                        onClick={() => handlePhotoClick(photo)}
                        onError={(e) => {
                          e.target.src = "https://via.placeholder.com/120?text=Image+Not+Found";
                        }}
                      />
                    ))}
                  </div>
                )}
              </li>
            );
          })
        ) : (
          <li className="no-reviews">No Reviews Yet</li>
        )}
      </ul>

      {reviews.length > reviewsPerPage && (
        <div className="pagination">
          <span>Showing {reviews.length} Reviews</span>
          <div className="pagination-buttons">
            <button
              className="page-btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                className={`page-btn ${currentPage === i + 1 ? "active" : ""}`}
                onClick={() => handlePageChange(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button
              className="page-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {selectedPhoto && (
        <div className="photo-modal" onClick={handleCloseModal}>
          <div className="modal-content">
            <img src={selectedPhoto} alt="Enlarged review photo" className="modal-image" />
            <button className="close-btn" onClick={handleCloseModal}>
              <FaTimes />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicemenReviews;